/**
 * MCP Key Service
 *
 * Frontend service for managing MCP API keys for AI assistant integration.
 * Communicates with the backend MCP key routes.
 */

import { apiRequest } from './api';
import type {
  ApiKeyScope,
  ApiKeyListItem,
  ApiKeyType,
  RateLimitTier,
  McpPermissionGroup,
  SystemApiKeyScope,
  CreateMcpKeyRequest,
  CreateSystemMcpKeyRequest,
} from '@simcheck/shared-types';

// ============================================================================
// Types
// ============================================================================

export interface McpKeyListItem {
  id: string;
  type: ApiKeyType;
  keyPrefix: string;
  name: string;
  description?: string;
  mcpPermissionGroup?: McpPermissionGroup;
  scopes: ApiKeyScope[];
  systemScopes?: SystemApiKeyScope[];
  rateLimitTier: RateLimitTier;
  status: 'active' | 'revoked' | 'expired';
  lastUsedAt?: Date;
  usageCount: number;
  createdAt: Date;
  expiresAt?: Date;
}

export interface McpKeyCreateResponse {
  mcpKey: {
    id: string;
    type: ApiKeyType;
    keyPrefix: string;
    name: string;
    description?: string;
    mcpPermissionGroup?: McpPermissionGroup;
    scopes: ApiKeyScope[];
    status: string;
    createdAt: string;
    expiresAt?: string;
  };
  key: string; // Full key value - ONLY shown once!
  message: string;
}

export interface McpKeyListResponse {
  mcpKeys: McpKeyListItem[];
  count: number;
}

export interface SystemMcpKeyCreateResponse {
  mcpKey: {
    id: string;
    type: ApiKeyType;
    keyPrefix: string;
    name: string;
    description?: string;
    systemScopes: SystemApiKeyScope[];
    rateLimitTier: RateLimitTier;
    status: string;
    createdAt: string;
    expiresAt?: string;
  };
  key: string; // Full key value - ONLY shown once!
  message: string;
}

// ============================================================================
// Organization MCP Key Functions
// ============================================================================

/**
 * List all MCP keys for the current organization
 */
export async function listMcpKeys(organizationId: string): Promise<McpKeyListResponse> {
  return apiRequest<McpKeyListResponse>('/mcp-keys', {}, organizationId);
}

/**
 * Get a single MCP key by ID
 */
export async function getMcpKey(id: string, organizationId: string): Promise<McpKeyListItem> {
  return apiRequest<McpKeyListItem>(`/mcp-keys/${id}`, {}, organizationId);
}

/**
 * Create a new MCP key for AI assistant integration
 * Returns the full key value - SAVE IT, this is the only time it's shown!
 */
export async function createMcpKey(
  request: CreateMcpKeyRequest,
  organizationId: string
): Promise<McpKeyCreateResponse> {
  return apiRequest<McpKeyCreateResponse>(
    '/mcp-keys',
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    organizationId
  );
}

/**
 * Revoke an MCP key (soft delete)
 */
export async function revokeMcpKey(
  id: string,
  organizationId: string,
  reason?: string
): Promise<{ message: string; keyId: string }> {
  return apiRequest(
    `/mcp-keys/${id}`,
    {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    },
    organizationId
  );
}

// ============================================================================
// System MCP Key Functions (Super Admin Only)
// ============================================================================

/**
 * List all system-level MCP keys
 * Requires super admin role
 */
export async function listSystemMcpKeys(): Promise<McpKeyListResponse> {
  return apiRequest<McpKeyListResponse>('/system-mcp-keys', {});
}

/**
 * Get a single system MCP key by ID
 * Requires super admin role
 */
export async function getSystemMcpKey(id: string): Promise<McpKeyListItem> {
  return apiRequest<McpKeyListItem>(`/system-mcp-keys/${id}`, {});
}

/**
 * Create a new system-level MCP key
 * Requires super admin role
 * Returns the full key value - SAVE IT, this is the only time it's shown!
 */
export async function createSystemMcpKey(
  request: CreateSystemMcpKeyRequest
): Promise<SystemMcpKeyCreateResponse> {
  return apiRequest<SystemMcpKeyCreateResponse>(
    '/system-mcp-keys',
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Revoke a system MCP key (soft delete)
 * Requires super admin role
 */
export async function revokeSystemMcpKey(
  id: string,
  reason?: string
): Promise<{ message: string; keyId: string }> {
  return apiRequest(
    `/system-mcp-keys/${id}`,
    {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }
  );
}

// Re-export types for convenience
export type {
  McpPermissionGroup,
  CreateMcpKeyRequest,
  CreateSystemMcpKeyRequest,
  SystemApiKeyScope,
};
